import { useEffect, useState } from "react";
import { Button } from "../../Button";
import _ from "lodash";
import { tMsg } from "../../ToastNotifications";
import { fieldId } from "../utils";
import { pause } from "../../../utils";
import classNames from "classnames";
import { UploadListModal } from "./UploadModal";

interface IDraggableStringProps {
  value: string;
  beingDragged: boolean;
  onDragStart: React.DragEventHandler<HTMLDivElement>;
  onDragEnter: React.DragEventHandler<HTMLDivElement>;
  onDragEnd: React.DragEventHandler<HTMLDivElement>;
  onDragLeave: React.DragEventHandler<HTMLDivElement>;
}

export const TextInputForStringArray = ({
  addValueFunction,
}: {
  addValueFunction: (val: string) => void;
}) => {
  const [inputVal, setInputVal] = useState<string>("");
  const modifyInput = (val: string) => {
    if (val.includes('"'))
      tMsg.warning('Cannot include double quotes " for this field');
    else setInputVal(val);
  };
  return (
    <div className="flex gap-3">
      <input
        value={inputVal}
        className="w-full border border-[#ccc] p-2.5 text-sm"
        placeholder="Type to add..."
        onChange={(e) => {
          e.preventDefault();
          modifyInput(e.target.value);
        }}
      />
      <Button
        onClick={(e) => {
          e.preventDefault();
          const temp = inputVal;
          addValueFunction(temp);
          setInputVal("");
        }}
        disabled={inputVal.length < 1}
      >
        Add
      </Button>
    </div>
  );
};

export const StringArrayItem = ({
  value,
  onDragEnd,
  onDragEnter,
  onDragStart,
  onDragLeave,
  beingDragged = false,
  ...restProps
}: React.ButtonHTMLAttributes<HTMLButtonElement> & IDraggableStringProps) => {
  return (
    <div
      draggable
      className={classNames("flex gap-[1px] w-full", {
        "opacity-40 cursor-grabbing": beingDragged,
      })}
      onDragOver={(e) => e.preventDefault()}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragEnd={onDragEnd}
    >
      <div
        className={classNames(
          "grid content-center w-full bg-grayBg px-3 py-0.5 text-sm cursor-grab",
          {
            "hover:bg-darkGrayBg": !beingDragged,
          }
        )}
      >
        {value}
      </div>
      <button
        {...restProps}
        className="bg-grayBg px-2 py-0.5 text-white hover:bg-darkGrayBg"
      >
        <i className="icon-close text-xs" />
      </button>
    </div>
  );
};

export const StringArrayField = ({
  name,
  onChange,
  array,
}: {
  name: string;
  onChange: (value: any) => void;
  array: any[];
}) => {
  const [hasCheckedQuotes, setHasCheckedQuotes] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [originIndex, setOriginIndex] = useState<number | null>(null);
  const [sortedItems, setSortedItems] = useState<any[]>(array.slice());

  // Upload stuff
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  useEffect(() => {
    removeQuotes();
  }, []);

  const removeQuotes = async () => {
    try {
      const checkForQuotes = array.map((v) => v.replaceAll('"', ""));
      onChange({ target: { value: checkForQuotes } });
      await pause(100);
    } finally {
      setHasCheckedQuotes(true);
    }
  };

  const addValueToList = (val: string) => {
    const valueToAdd = [...array, val];
    onChange({ target: { value: valueToAdd } });
  };

  const removeValueFromList = (indexToRemove: number) => {
    const newList = array.filter((a, index) => index !== indexToRemove);
    onChange({ target: { value: newList } });
  };

  const removeAllDonors = () => {
    onChange({ target: { value: [] } });
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.stopPropagation();
    setActiveIndex(index);
    setOriginIndex(index);
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (activeIndex === null) return;
    if (index === activeIndex) return;

    const newArray = sortedItems;
    newArray.splice(index, 0, newArray.splice(activeIndex, 1)[0]);
    setActiveIndex(index);
    setSortedItems(newArray);
  };

  const handleOnDragLeave = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setActiveIndex(originIndex);
    setSortedItems(array.slice());
  };

  const handleDragEnd = (e: React.DragEvent, index: number) => {
    if (activeIndex === null) return;
    onChange({ target: { value: sortedItems } });
    setActiveIndex(null);
    setOriginIndex(null);
  };

  const displayArray = activeIndex !== null ? sortedItems : array;

  return (
    <div id={fieldId(name)} className="space-y-4 pb-4 p-3 bg-gray-200">
      <TextInputForStringArray addValueFunction={addValueToList} />
      <div className="grid grid-cols-2 gap-3">
        {hasCheckedQuotes &&
          displayArray.map((donor, index) => (
            <StringArrayItem
              value={donor}
              key={index}
              onClick={(e) => {
                e.preventDefault();
                removeValueFromList(index);
              }}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragEnd={(e) => handleDragEnd(e, index)}
              onDragLeave={(e) => handleOnDragLeave(e, index)}
              beingDragged={activeIndex !== null && activeIndex === index}
            />
          ))}
      </div>
      <div className="text-sm text-grayText my-2 font-medium flex justify-between">
        {array.length > 0 ? (
          <div>
            <div>Total items: {array.length}</div>
            <button
              onClick={(e) => {
                e.preventDefault();
                removeAllDonors();
              }}
              className="text-sm underline text-darkGrayBg hover:text-grayText"
            >
              Remove All
            </button>
          </div>
        ) : (
          "No items - use the input above to add a donor"
        )}
        <Button
          onClick={(e) => {
            e.preventDefault();
            setShowUploadModal(true);
          }}
        >
          Use upload
        </Button>
        {showUploadModal && (
          <UploadListModal
            show={showUploadModal}
            setShowExternal={setShowUploadModal}
            setStrings={(value: string[]) => onChange({ target: { value } })}
          />
        )}
      </div>
    </div>
  );
};
